import { computed, defineComponent, ref } from '@vue/composition-api';
import { isAutomated } from '@/config/isAutomated';
// The QA routes live at the backend ORIGIN, not under /api. The app is
// configured with the API base (`http://localhost:8000/api/`), so the origin is
// that with the trailing /api/ removed.
const qaOrigin = () => String(process.env.VUE_APP_PUBLIC_API_URL || '').replace(/\/api\/?$/, '');
export default defineComponent({
    name: 'DevLoginPanel',
    setup() {
        const open = ref(false);
        const busy = ref(false);
        const message = ref('');
        const roles = [
            { id: 'admin', label: 'Admin' },
            { id: 'vendor', label: 'Vendor' },
            { id: 'customer', label: 'Customer' }
        ];
        // Never render while a test runner is driving the browser.
        //
        // This is the whole of the "must not conflict with the tests" requirement,
        // and it is deliberately a render gate rather than a styling one: with
        // `v-if` false there is no extra node in the DOM at all, so no selector
        // count changes, nothing shifts layout, and no spec can accidentally match
        // it. The signal is `navigator.webdriver` (config/isAutomated.ts). It used
        // to be `window.Cypress`, which nothing sets since Cypress was removed, and
        // the panel then rendered into every Playwright page.
        //
        // NODE_ENV is the WRONG signal here and it is worth saying why, because
        // it is the obvious choice and it does not work. Every bundle this project
        // deploys -- including the one served on localhost -- is produced by
        // `vue-cli-service build`, which sets NODE_ENV=production. Gating on it
        // would hide the panel in exactly the environment it exists for.
        //
        // So: a local host turns it on automatically, and anywhere else it takes a
        // deliberate opt-in stored in the browser:
        //
        //     localStorage.setItem('asq_dev_tools', '1')
        //
        // That matters because the tunnel host is publicly reachable and the /qa
        // routes behind this panel are currently reachable there too
        // (backend-qa-guard emits no Caddy route while TEST_MODE is on). The panel
        // does not create that exposure, but it should not advertise it to every
        // visitor either. Opt-in keeps it invisible to anyone who has not asked.
        const devHost = () => {
            const h = window.location.hostname;
            return (h === 'localhost' ||
                h === '127.0.0.1' ||
                h === '[::1]' ||
                h.endsWith('.localhost'));
        };
        const optedIn = () => {
            try {
                return localStorage.getItem('asq_dev_tools') === '1';
            }
            catch (e) {
                // Private windows and blocked site data throw on access.
                return false;
            }
        };
        const visible = computed(() => typeof window !== 'undefined' &&
            !isAutomated() &&
            (devHost() || optedIn()));
        const applySession = (accessToken, refreshToken) => {
            // Merge rather than replace. `setupVuexStore` persists BOTH
            // `authentication` and `booking` under the single `vuex` key, so writing
            // a fresh object here would silently discard an in-progress booking.
            let store = {};
            try {
                store = JSON.parse(localStorage.getItem('vuex') || '{}');
            }
            catch (e) {
                store = {};
            }
            store.authentication = { accessToken, refreshToken };
            localStorage.setItem('vuex', JSON.stringify(store));
        };
        const loginAs = async (role) => {
            busy.value = true;
            message.value = '';
            try {
                const response = await fetch(`${qaOrigin()}/qa/account/login/role?role=${role}`);
                const body = await response.json();
                if (!response.ok) {
                    // The route answers 404 with a hint when the database has been
                    // truncated, which is the common case while a Cypress run is in
                    // flight. Show it instead of a bare status code.
                    message.value = body.hint
                        ? `${body.error} -- ${body.hint}`
                        : body.error || `HTTP ${response.status}`;
                    return;
                }
                applySession(body.access_token, body.refresh_token);
                window.location.reload();
            }
            catch (e) {
                message.value = `Request failed: ${String(e)}`;
            }
            finally {
                busy.value = false;
            }
        };
        const logout = () => {
            let store = {};
            try {
                store = JSON.parse(localStorage.getItem('vuex') || '{}');
            }
            catch (e) {
                store = {};
            }
            delete store.authentication;
            localStorage.setItem('vuex', JSON.stringify(store));
            window.location.reload();
        };
        return { open, busy, message, roles, visible, loginAs, logout };
    }
});
